import { ASSET_ORIGIN } from '@/config'

export function resolveAssetUrl(path) {
  if (!path) return ''
  if (/^https?:\/\//.test(path)) return path
  return `${ASSET_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`
}

export function formatSessionTime(timestamp) {
  if (!timestamp) return ''
  const ts = Number(timestamp)
  if (!ts) return ''
  const date = new Date(ts * 1000)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const p = (n) => String(n).padStart(2, '0')
  if (isToday) {
    return `${p(date.getHours())}:${p(date.getMinutes())}`
  }
  return `${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}`
}

export function formatLastMessage(lastMessage) {
  if (!lastMessage) return '暂无消息'
  if (typeof lastMessage === 'string') return lastMessage
  if (lastMessage.content) return lastMessage.content
  if (lastMessage.text) return lastMessage.text
  if (lastMessage.nickname || lastMessage.mobile) {
    return [lastMessage.nickname, lastMessage.mobile].filter(Boolean).join(' ')
  }
  return '新消息'
}

export function mapSessionItem(raw) {
  const type = String(raw.type) === '1' ? 'single' : 'batch'
  return {
    id: String(raw.session_id),
    sessionId: raw.session_id,
    taxChatPoolId: raw.tax_chat_pool_id,
    toUserId: null,
    type,
    apiType: raw.type,
    name: raw.display_name || '未命名会话',
    avatarUrl: resolveAssetUrl(raw.avatar),
    avatarColor: '#5B8FF9',
    lastMessage: formatLastMessage(raw.last_message),
    time: formatSessionTime(raw.last_time),
    unread: Number(raw.unread) || 0,
    memberCount: raw.member_count || 0,
    status: 'talking',
    business: type === 'batch' ? '批量业务' : '单人业务',
    region: raw.member_count ? `${raw.member_count} 人` : '',
    source: '小程序',
    phone: '',
    wechat: '',
    firstConsult: formatSessionTime(raw.last_time),
    remark: '',
    isNew: false,
    verified: true,
    requirements: {
      companyType: '-',
      scope: '-',
      address: '-',
      extra: '-'
    },
    plan: {
      title: '待对接方案',
      status: '沟通中',
      id: '-',
      location: '-',
      desc: '请先在会话中了解客户需求',
      price: '0.00',
      count: 0
    },
    logs: [],
    messages: [],
    messagesLoaded: false
  }
}

export function createSessionDetail(base) {
  if (!base) return null
  return {
    ...base,
    messages: base.messages,
    messagesLoaded: !!base.messagesLoaded,
    logs: base.logs || [],
    requirements: base.requirements || {
      companyType: '-',
      scope: '-',
      address: '-',
      extra: '-'
    },
    plan: base.plan || {
      title: '待对接方案',
      status: '沟通中',
      id: '-',
      location: '-',
      desc: '请先在会话中了解客户需求',
      price: '0.00',
      count: 0
    }
  }
}

export function mapPlanFromSavedQuote(saved) {
  if (!saved) return null
  const location = String(saved.regionText || '')
    .replace(/\s*\/\s*/g, '')
    .trim() || '-'
  const serviceName = saved.serviceName || ''
  const categoryName = saved.categoryName || ''
  const title = [categoryName, serviceName].filter(Boolean).join(' · ') || '待对接方案'
  let desc = '已生成报价，可发送给客户'
  if (Array.isArray(saved.includeList) && saved.includeList.length) {
    desc = saved.includeList.slice(0, 3).join('、')
    if (saved.includeList.length > 3) desc += '等'
  } else if (serviceName) {
    desc = `${serviceName}报价方案`
  }
  const price = saved.totalPrice != null ? String(saved.totalPrice) : '0.00'
  const count = Array.isArray(saved.bizList) && saved.bizList.length
    ? saved.bizList.length
    : 1
  return {
    title,
    status: '待发送报价',
    id: saved.planId || (saved.poolId ? `Q${saved.poolId}` : '-'),
    location,
    desc,
    price,
    count
  }
}

export function readSavedQuote(poolId) {
  if (!poolId) return null
  try {
    const raw = sessionStorage.getItem(`quote_saved_${poolId}`)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

/** 更新本地已保存报价的金额 */
export function updateSavedQuotePrice(poolId, price) {
  const saved = readSavedQuote(poolId)
  if (!saved || !poolId) return null
  const next = {
    ...saved,
    totalPrice: String(price)
  }
  try {
    sessionStorage.setItem(`quote_saved_${poolId}`, JSON.stringify(next))
  } catch (e) {
    // ignore
  }
  return next
}

export function applySavedQuoteToSession(session) {
  if (!session || !session.taxChatPoolId) return session
  const saved = readSavedQuote(session.taxChatPoolId)
  const plan = mapPlanFromSavedQuote(saved)
  if (plan) {
    session.plan = plan
  }
  return session
}

export function parseJwtPayload(token) {
  if (!token) return {}
  try {
    const base64 = token.split('.')[1]
    const json = decodeURIComponent(
      atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return {}
  }
}

export function formatChatLogTime(datetime) {
  if (datetime == null || datetime === '') return ''
  let date
  if (typeof datetime === 'number' || /^\d+$/.test(String(datetime))) {
    const ts = Number(datetime)
    date = new Date(ts < 1e12 ? ts * 1000 : ts)
  } else {
    date = new Date(String(datetime).replace(/-/g, '/'))
  }
  if (Number.isNaN(date.getTime())) return String(datetime)
  const now = new Date()
  const p = (n) => String(n).padStart(2, '0')
  if (date.toDateString() === now.toDateString()) {
    return `${p(date.getHours())}:${p(date.getMinutes())}`
  }
  return `${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}`
}

export function parseAddressData(message) {
  if (!message || typeof message !== 'object') return null
  const region = [message.province_name, message.city_name, message.area_name].filter(Boolean).join('')
  const detail = message.address || ''
  return {
    receiver: message.nickname || message.name || '-',
    phone: message.mobile || '-',
    address: `${region}${detail}` || '-'
  }
}

/** 解析订单消息为卡片字段 */
export function parseOrderData(message, fallback = {}) {
  if (message && typeof message === 'object') {
    const title = message.service_name || message.title || message.name || fallback.title || ''
    const parts = String(title).split(/\s*·\s*/).filter(Boolean)
    const category = message.category_name || message.category || parts[0] || '-'
    const service =
      message.service_detail ||
      message.sub_service_name ||
      (parts.length > 1 ? parts.slice(1).join(' · ') : (message.service_name || parts[0] || '-'))
    const area =
      message.area_name ||
      message.region ||
      [message.province_name, message.city_name, message.area_name].filter(Boolean).join('') ||
      fallback.area ||
      '-'
    const priceRaw = message.price != null ? message.price : fallback.price
    const price = priceRaw == null || priceRaw === ''
      ? '-'
      : String(priceRaw).startsWith('¥')
        ? String(priceRaw)
        : `¥${priceRaw}`

    const orderNo =
      message.order_sn ||
      message.sn ||
      message.end_order_sn ||
      message.order_no ||
      (message.id != null ? String(message.id) : '') ||
      fallback.orderNo ||
      ''

    const endRaw =
      message.end_price != null && message.end_price !== ''
        ? message.end_price
        : message.tail_price != null && message.tail_price !== ''
          ? message.tail_price
          : message.final_price
    const endNum = Number(endRaw)
    const statusText = String(message.status_text || message.state_text || message.status_name || '')
    const cardType =
      fallback.cardType === 'balance' ||
      message.card_type === 'balance' ||
      message.pay_type === 'end' ||
      message.pay_stage === 'end' ||
      /尾款/.test(statusText) ||
      (Number.isFinite(endNum) && endNum > 0 && /待付|待支付/.test(statusText))
        ? 'balance'
        : 'quote'

    const balanceRaw =
      endRaw != null && endRaw !== ''
        ? endRaw
        : fallback.balanceAmount != null
          ? fallback.balanceAmount
          : fallback.balance || ''
    const balance =
      balanceRaw === '' || balanceRaw == null
        ? ''
        : String(balanceRaw).startsWith('¥')
          ? String(balanceRaw)
          : `¥${balanceRaw}`

    return {
      cardType,
      category,
      service,
      area,
      price: cardType === 'balance' && balance ? balance : price,
      balance,
      orderNo,
      orderId: message.id || message.order_id || fallback.orderId || ''
    }
  }

  if (typeof message === 'string') {
    const text = message.replace(/^\[订单\]\s*/, '')
    const parts = text.split(/\s*·\s*/).filter(Boolean)
    const pricePart = parts.find((p) => /¥|￥|\d/.test(p) && /¥|￥|\d+(\.\d+)?/.test(p))
    const nonPrice = parts.filter((p) => p !== pricePart)
    return {
      cardType: fallback.cardType || 'quote',
      category: nonPrice[0] || fallback.title || '-',
      service: nonPrice.slice(1).join(' · ') || nonPrice[0] || '-',
      area: fallback.area || '-',
      price: pricePart
        ? (String(pricePart).startsWith('¥') || String(pricePart).startsWith('￥') ? pricePart : `¥${pricePart}`)
        : (fallback.price || '-'),
      balance: fallback.balance || '',
      orderNo: fallback.orderNo || '',
      orderId: fallback.orderId || ''
    }
  }

  if (fallback.title || fallback.price || fallback.balance) {
    return parseOrderData(fallback.title || '', fallback)
  }
  return null
}

export function formatChatMessage(message, messageType) {
  if (messageType === 'text' || typeof message === 'string') {
    return message || ''
  }
  if (messageType === 'order' && message && typeof message === 'object') {
    const parts = [
      message.service_name,
      message.area_name,
      message.price ? `¥${message.price}` : ''
    ].filter(Boolean)
    return `[订单] ${parts.join(' · ')}`
  }
  if (messageType === 'address' && message && typeof message === 'object') {
    const region = [message.province_name, message.city_name, message.area_name].filter(Boolean).join('')
    const contact = [message.nickname, message.mobile].filter(Boolean).join(' ')
    return `[地址] ${region}${message.address || ''}${contact ? `（${contact}）` : ''}`
  }
  if (message && typeof message === 'object') {
    return JSON.stringify(message)
  }
  return String(message || '')
}

/** 解析 WebSocket 推送的聊天消息体 */
export function normalizeWsChatPayload(raw) {
  if (!raw || typeof raw !== 'object') return null
  let data = { ...raw }
  // 去掉 store 附加字段
  delete data.timestamp
  delete data._trackId
  if (data.data && typeof data.data === 'object') {
    data = { ...data, ...data.data }
  }
  const skipTypes = ['error', 'login_success', 'login_error', 'login', 'ping', 'pong', 'read', 'init']
  if (skipTypes.includes(data.type)) return null
  // 聊天帧：有 message / message_type，或 type=chat/new_message
  const isChat =
    data.type === 'chat' ||
    data.type === 'new_message' ||
    data.type === 'message' ||
    data.message_type != null ||
    (data.message !== undefined && (data.sender_id || data.to_user_id || data.tax_chat_pool_id || data.session_id))
  if (!isChat) return null
  if (data.message === undefined && data.content === undefined) return null
  if (data.message === undefined && data.content !== undefined) {
    data.message = data.content
  }
  return data
}

/** 根据 WS 消息匹配本地会话 */
export function findSessionForWsMessage(sessions, data, currentUserId) {
  const sessionId = data.session_id || data.sessionId
  const poolId = data.tax_chat_pool_id || data.taxChatPoolId
  const uid = Number(currentUserId)
  const senderId = Number(data.sender_id || (data.sender && data.sender.id) || data.from_user_id)
  const toUserId = Number(data.to_user_id)

  if (sessionId) {
    const bySession = sessions.find((item) => String(item.sessionId) === String(sessionId))
    if (bySession) return bySession
  }
  if (poolId) {
    const byPool = sessions.find((item) => String(item.taxChatPoolId) === String(poolId))
    if (byPool) return byPool
  }
  if (senderId && senderId !== uid) {
    const bySender = sessions.find((item) => Number(item.toUserId) === senderId)
    if (bySender) return bySender
  }
  if (toUserId && toUserId !== uid) {
    const byReceiver = sessions.find((item) => Number(item.toUserId) === toUserId)
    if (byReceiver) return byReceiver
  }
  return null
}

export function mapWsChatMessage(raw, currentUserId) {
  if (!raw) return null
  const uid = Number(currentUserId)
  const senderId = Number(raw.sender_id || (raw.sender && raw.sender.id) || raw.from_user_id)
  const isStaff = uid && senderId === uid
  const sender = raw.sender || {}
  const messageType = raw.message_type || 'text'
  const text = formatChatMessage(raw.message, messageType)
    return {
      id: raw.id || `ws-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      senderId,
      role: isStaff ? 'staff' : 'customer',
      text,
      messageType,
      addressData: messageType === 'address' ? parseAddressData(raw.message) : null,
      orderData: messageType === 'order' ? parseOrderData(raw.message, { title: text }) : null,
      rawMessage: messageType === 'order' || messageType === 'address' ? raw.message : null,
      time: formatChatLogTime(raw.createtime || raw.time) || formatNowTime(),
      avatarUrl: sender.full_avatar || sender.avatar || '',
      senderName: sender.nickname || '',
      read: isStaff
    }
}

function formatNowTime() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}`
}

export function extractCustomerUserId(list, currentUserId) {
  const uid = Number(currentUserId)
  const item = (list || []).find((raw) => Number(raw.sender_id) && Number(raw.sender_id) !== uid)
  return item ? Number(item.sender_id) : null
}

export function mapChatLogs(list, currentUserId) {
  const uid = Number(currentUserId)
  const systemMsg = {
    id: 'system-tip',
    role: 'system',
    text: '系统提醒：请文明用语，及时响应客户的问题'
  }
  const items = (list || []).map((raw) => {
    const senderId = Number(raw.sender_id || (raw.sender && raw.sender.id))
    const isStaff = uid && senderId === uid
    const sender = raw.sender || {}
    const messageType = raw.message_type || 'text'
    const text = formatChatMessage(raw.message, messageType)
    return {
      id: raw.id,
      senderId,
      role: isStaff ? 'staff' : 'customer',
      text,
      messageType,
      addressData: messageType === 'address' ? parseAddressData(raw.message) : null,
      orderData: messageType === 'order' ? parseOrderData(raw.message, { title: text }) : null,
      rawMessage: messageType === 'order' || messageType === 'address' ? raw.message : null,
      time: formatChatLogTime(raw.createtime),
      avatarUrl: sender.full_avatar || sender.avatar || '',
      senderName: sender.nickname || '',
      read: isStaff
    }
  })
  return [systemMsg, ...items]
}
